import React, { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';

const ImageInput = ({
    onChange,
    error,
    showLabel = true,
    label = "Image",
    required = false,
    className = "",
    multiple = false,
    showPreview = false,
    onProgress,
    existingImage = null, // URL of existing image for edit mode
    compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
    }
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewUrls, setPreviewUrls] = useState([]);
    const fileInputRef = useRef(null);

    const clearPreviews = () => {
        setPreviewUrls((prev) => {
            prev.forEach((url) => {
                if (typeof url === 'string' && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            return [];
        });
    };

    // Function to convert image to WebP
    const convertToWebP = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);

                    // Convert to WebP
                    canvas.toBlob((blob) => {
                        if (blob) {
                            // Create a new file with .webp extension
                            const webpFile = new File(
                                [blob],
                                file.name.replace(/\.[^/.]+$/, '.webp'),
                                {
                                    type: 'image/webp',
                                    lastModified: new Date().getTime()
                                }
                            );
                            resolve(webpFile);
                        } else {
                            reject(new Error('WebP conversion failed'));
                        }
                    }, 'image/webp', 0.8);
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    // Function to handle image compression and conversion
    const handleImageCompression = async (imageFile) => {
        try {
            // First compress the image
            const compressedFile = await imageCompression(imageFile, {
                ...compressionOptions,
                onProgress: (progress) => {
                    // Scale compression progress to 0-80% (compression takes 80% of total time)
                    const scaledProgress = Math.round(progress * 0.8);
                    setProgress(scaledProgress);
                    onProgress?.(scaledProgress);
                }
            });

            // Update progress to 90% for WebP conversion
            setProgress(90);
            onProgress?.(90);

            // Then convert to WebP
            const webpFile = await convertToWebP(compressedFile);

            // Complete progress
            setProgress(100);
            onProgress?.(100);

            return webpFile;
        } catch (error) {
            console.error('Error processing image:', error);
            throw error;
        }
    };

    // Handle file selection
    const handleFileChange = async (event) => {
        if (!event?.target?.files?.length) {
            clearPreviews();
            onChange(null);
            return;
        }

        const files = Array.from(event.target.files);
        setIsProcessing(true);
        setProgress(0);
        onProgress?.(0);

        try {
            if (multiple) {
                // Process multiple files
                const processedImages = await Promise.all(
                    files.map(async (file, index) => {
                        if (!file.type.startsWith('image/')) return null;
                        
                        // Calculate progress for each file
                        const fileProgress = (index / files.length) * 100;
                        setProgress(fileProgress);
                        onProgress?.(fileProgress);
                        
                        return await handleImageCompression(file);
                    })
                );
                const validImages = processedImages.filter(img => img !== null);
                clearPreviews();
                onChange(validImages);
            } else {
                // Process single file
                const file = files[0];
                if (!file.type.startsWith('image/')) {
                    clearPreviews();
                    onChange(null);
                    return;
                }
                const processedImage = await handleImageCompression(file);
                clearPreviews();
                setPreviewUrls([URL.createObjectURL(processedImage)]);
                onChange(processedImage);
            }
        } catch (error) {
            console.error('Error processing image:', error);
            clearPreviews();
            onChange(null);
        } finally {
            setIsProcessing(false);
            // Keep progress at 100% briefly before resetting
            setTimeout(() => {
                setProgress(0);
            }, 1000);
        }
    };

    // Handle custom button click
    const handleCustomButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className={className}>
          
            {showLabel && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div className="space-y-0">
                {/* Hidden File Input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple={multiple}
                    onChange={handleFileChange}
                    disabled={isProcessing}
                    className="hidden"
                />
                
                {/* Custom File Input Design */}
                <div className="relative">
                    <button
                        type="button"
                        onClick={handleCustomButtonClick}
                        disabled={isProcessing}
                        className={`w-full px-2 py-1 h-8 border ${
                            error ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:outline-none focus:border-primary focus:ring-none transition-colors ${
                            isProcessing ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-gray-400 bg-white'
                        } flex items-center justify-between text-left`}
                    >
                        <span className="text-sm text-gray-500 truncate">
                            {isProcessing ? 'Processing...' : 'Choose file'}
                        </span>
                        <div className="flex items-center space-x-2">
                            {isProcessing && (
                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            )}
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        </div>
                    </button>
                    
                    {/* Progress Bar Overlay */}
                    {isProcessing && (
                        <div className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex flex-col justify-center items-center p-4">
                            <div className="w-full">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-medium text-primary">
                                        Processing image...
                                    </span>
                                    <span className="text-xs font-medium text-primary">
                                        {progress}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <p className="text-red-500 text-xs mt-1">
                        {error}
                    </p>
                )}

                {/* Preview (single-image only; multi-image forms use FilePreview below) */}
                {showPreview && !multiple && (previewUrls.length > 0 || existingImage) && (
                    <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">
                            Preview
                        </div>
                        {multiple ? (
                            <div className="grid grid-cols-3 gap-2">
                                {previewUrls.map((url, idx) => (
                                    <img
                                        key={idx}
                                        src={url}
                                        alt={`preview-${idx}`}
                                        className="w-full h-20 object-cover rounded-md border"
                                    />
                                ))}
                            </div>
                        ) : (
                            <img
                                src={previewUrls[0] || existingImage}
                                alt="preview"
                                className="w-24 h-24 object-cover rounded-md border"
                            />
                        )}
                        {existingImage && previewUrls.length === 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                                Current image
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageInput; 